import { RugbySummaryApi, MotorsportSummaryApi, CricketSummaryApi, FootballSummaryApi, TennisSummaryApi } from '@/api/validators/score';
import { SportSummaryType, Summary } from '@/components/Sport/types';
import { GolfStrokeSummaryApi, GolfMatchPlaySummaryApi } from '../validators/golf';

export const convertFootballSummary = (summary: FootballSummaryApi) => {
  const convertedSummary = {
    ...summary,
    channel: summary.channelDetails?.[0].channel,
  };
  return convertedSummary;
};
export type FootballSummary = ReturnType<typeof convertFootballSummary>;

export const convertRugbySummary = (summary: RugbySummaryApi) => {
  const warningSubStatus = ['Postponed', 'Cancelled', 'Suspended', 'Delayed'];
  const subStatusId = summary.matchSubStatus == null ? 0 : warningSubStatus.includes(summary.matchSubStatus) ? 4 : 1;

  const sTotal = summary.score.total;
  const winnerSide = summary.status.id !== 3
    ? null
    : sTotal.home === sTotal.away
      ? 'draw'
      : (sTotal.home || 0) > (sTotal.away || 0)
        ? 'home'
        : 'away';

  return {
    eventDateStart: summary.eventDateStart,
    score: {
      total: summary.score.total,
      shootout: null,
      winner: {
        side: winnerSide as 'draw' | 'home' | 'away',
      },
    },
    status: summary.status,
    subStatus: {
      id: subStatusId,
      name: summary.matchSubStatus || '',
    },
    teams: {
      home: {
        ...summary.rugbyTeam.home,
        shortName: summary.rugbyTeam.home.name,
      },
      away: {
        ...summary.rugbyTeam.away,
        shortName: summary.rugbyTeam.away.name,
      },
    },
    eventId: summary.eventId,
    tournament: summary.tour,
    venueName: summary.venue.venueName,
    channel: summary.channelDetails?.[0].channel,
    feedId: summary.feedId,
  };
};
export type RugbySummary = ReturnType<typeof convertRugbySummary>;

export const convertCricketSummary = (summary: CricketSummaryApi) => {
  let [home, away] = summary.scores;
  [home, away] = home.team.teamId === summary.homeTeamId ? [home, away] : [away, home];

  const battingTeam = summary.battingTeamId != null ? summary.battingTeamId === summary.homeTeamId ? 'home' : 'away' : undefined;
  const winnerSide = summary.winnerTeamId != null && summary.winnerTeamId !== ''
    ? summary.winnerTeamId === summary.homeTeamId
      ? 'home'
      : 'away'
    : undefined;

  return {
    eventDateStart: summary.eventDateStart,
    eventDateEnd: summary.eventDateEnd,
    score: {
      home: home.inningsScores,
      away: away.inningsScores,
      winner: {
        side: winnerSide as 'home' | 'away' | undefined,
      },
    },
    status: summary.status,
    subStatus: summary.subStatus,
    gameType: summary.gameType,
    battingTeam: battingTeam as 'home' | 'away' | undefined,
    resultDescription: summary.resultDescription,
    teams: {
      home: home.team,
      away: away.team,
    },
    eventId: summary.eventId,
    tournament: summary.tour,
    eventName: summary.eventName,
    venueName: summary.venue.venueName,
    channel: summary.channelDetails?.[0].channel,
    toss: summary.toss,
  };
};
export type CricketSummary = ReturnType<typeof convertCricketSummary>;

export const convertMotorsportSummary = (summary: MotorsportSummaryApi) => {
  const convertedSummary = {
    eventDateStart: summary.eventDateStart,
    eventName: summary.event.name,
    feedId: summary.id,
    eventId: summary.event.eventId,
    status: summary.status,
    raceResult: summary.raceResult,
    countryName: summary.country,
    cityName: summary.city,
    tournament: summary.tour,
    venueName: summary.venue.venueName,
    subStatus: summary.subStatus,
    channel: summary.channelDetails?.[0].channel,
  };
  return convertedSummary;
};
export type MotorsportSummary = ReturnType<typeof convertMotorsportSummary>;

export const convertGolfStrokeSummary = (summary: GolfStrokeSummaryApi) => {
  return {
    ...summary,
  };
};
export type GolfStrokeSummary = ReturnType<typeof convertGolfStrokeSummary>;
export const convertGolfMatchPlaySummary = (summary: GolfMatchPlaySummaryApi) => {
  return summary;
};
export type GolfMatchPlaySummary = ReturnType<typeof convertGolfMatchPlaySummary>;

export const convertTennisSummary = (summary: TennisSummaryApi) => {
  return {
    eventDateStart: summary.eventDateStart,
    eventDateEnd: summary.eventDateEnd,
    status: summary.status,
    subStatus: summary.subStatus,
    round: summary.round,
    tour: summary.tour,
    tournament: summary.tournament,
    surfaceType: summary.surfaceType,
    tourName: summary.tourName,
    gender: summary.gender,
    matchType: summary.matchType,
    icon: summary.icon,
    eventId: summary.eventId,
    eventName: summary.eventName,
    venueName: summary.venue.venueName,
    feedId: summary.feedId,
    channel: summary.channelDetails?.[0].channel,
  };
};
export type TennisSummary = ReturnType<typeof convertTennisSummary>;

export const convertSportSummary = <
  T extends SportSummaryType,
  U extends
      T extends 'cricket'
    ? CricketSummaryApi
    : T extends 'rugby'
    ? RugbySummaryApi
    : T extends 'motorsport'
    ? MotorsportSummaryApi
    : T extends 'golf'
    ? GolfStrokeSummaryApi
    : T extends 'football'
    ? FootballSummaryApi
    : T extends 'tennis'
    ? TennisSummaryApi
    : any
>(sport: T, summary: U): Summary => {
  switch (sport) {
    case 'football':
      return convertFootballSummary(summary);
    case 'rugby':
      return convertRugbySummary(summary);
    case 'cricket':
      return convertCricketSummary(summary);
    case 'motorsport':
      return convertMotorsportSummary(summary);
    case 'golf':
      if (summary.data != null) {
        return  convertGolfStrokeSummary(summary);
      }
      return  convertGolfMatchPlaySummary(summary);
    case 'tennis':
      return convertTennisSummary(summary);
    default:
      return summary;
  }
};
